import React, {
  Fragment,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { Link } from "react-router-dom";

import { AppliedIcon } from "@/Components/Icons/candidateType/AppliedIcon";
import { BookedIcon } from "@/Components/Icons/candidateType/BookedIcon";
import { InvitedIcon } from "@/Components/Icons/candidateType/InvitedIcon";
import { UnfilledIcon } from "@/Components/Icons/candidateType/UnfilledIcon";
import { WorkedIcon } from "@/Components/Icons/candidateType/WorkedIcon";
import {
  DEFAULT_BOOKED_LIST_VIEW,
  DEFAULT_WORKED_LIST_VIEW,
} from "@/Constants/common";
import { updateFilters as updateAppliedFilters } from "@/reducers/client_candidates/candidatesapplied";
import {
  updateFilters as updateBookedFilters,
  updateListView as updateBookedView,
} from "@/reducers/client_candidates/candidatesbooked";
import { updateFilters as updateInvitedFilters } from "@/reducers/client_candidates/candidatesinvited";
import {
  updateFilters as updateWorkedFilters,
  updateListView as updateWorkedView,
} from "@/reducers/client_candidates/candidatesworked";
import { toggleOurServiceModal } from "@/reducers/clientcommon";
import { fetchCandidateTypes } from "@/reducers/clientdashboard";
import { updateFilters as updateUnfilledFilters } from "@/reducers/clientunfilledcontracts";
import { showManagedServiceToast } from "@/utilities/utils";

import NotAllowedButtonWrapper from "../managedService/NotAllowedButtonWrapper";
import NoAccessButtonWrapper from "../UserRoleAccess/NoAccessButtonWrapper";

/**
 * React component for rendering candidate types with icons and counts.
 *
 * @return {JSX.Element} The JSX element representing the candidate types component.
 */
const ICON_OBJ = {
  invited: {
    icon: <InvitedIcon cls="h-[22px] w-[22px] lg:h-[25px] lg:w-[25px]" />,
    color: "text-cyan",
    bg: "bg-cyan-200",
    active: "bg-cyan",
    hoverBg: "hover:bg-cyan",
  },
  unfilled: {
    icon: <UnfilledIcon cls="h-[22px] w-[22px] lg:h-[25px] lg:w-[25px]" />,
    color: "text-red",
    bg: "bg-red-200",
    active: "bg-red",
    hoverBg: "hover:bg-red",
  },
  applied: {
    icon: <AppliedIcon cls="h-[22px] w-[22px] lg:h-[25px] lg:w-[25px]" />,
    color: "text-yellow",
    bg: "bg-yellow-200",
    active: "bg-yellow",
    hoverBg: "hover:bg-yellow",
  },
  booked: {
    icon: <BookedIcon cls="h-[22px] w-[22px] lg:h-[25px] lg:w-[25px]" />,
    color: "text-green",
    bg: "bg-green-200",
    active: "bg-green",
    hoverBg: "hover:bg-green",
  },
  worked: {
    icon: <WorkedIcon cls="h-[22px] w-[22px] lg:h-[25px] lg:w-[25px]" />,
    color: "text-primary",
    bg: "bg-primary-200",
    active: "bg-primary",
    hoverBg: "hover:bg-primary",
  },
};

const CandidateTypeLink = React.memo(function CandidateTypeLink({
  ele,
  activeSubPage,
  clientUserAllPermissions,
  isPremiumAccount,
  client_allow_publish_job,
  user,
  dispatch,
}) {
  const mainObj = ICON_OBJ[ele.type];
  const isPremiumAccountPage =
    ele.name === "unfilled" || ele.name === "applied";
  // const hasAccess = clientUserAllPermissions[ele.access];
  const managedServicePremium = isPremiumAccount
    ? isPremiumAccount
    : client_allow_publish_job;

  const handleClick = useCallback(
    (e) => {
      if (isPremiumAccountPage && !managedServicePremium) {
        e.preventDefault();
        dispatch(toggleOurServiceModal(true));
        showManagedServiceToast();
      }
    },
    [isPremiumAccountPage, managedServicePremium, dispatch],
  );

  // Memoize rendered label to avoid recalculation
  const label = useMemo(() => {
    if (ele.name === "invited") {
      if (isPremiumAccount) {
        if (user.is_bank_client) return "Bank Invites";
        return "Invited";
      }
      return "Invited";
    }
    if (ele.name == "applied") {
      return "Proposed";
    }
    return ele.name;
  }, [ele.name, isPremiumAccount, user.is_bank_client]);

  return (
    <Link
      to={isPremiumAccountPage && !managedServicePremium ? "#" : ele.link}
      className={`inline-block w-full ${isPremiumAccountPage && !managedServicePremium ? "" : "group/tab"} ${
        activeSubPage === ele.name
          ? mainObj.active
          : `bg-white ${isPremiumAccountPage && !managedServicePremium ? "" : mainObj.hoverBg}`
      } tour-invited-clientsidebar rounded-[14px] p-3 !decoration-none  hover:decoration-none lg:px-4 lg:py-3 xl:px-5 xl:py-4 ${
        isPremiumAccountPage && !managedServicePremium ? "opacity-50" : ""
      }`}
      onClick={handleClick}
      tabIndex={isPremiumAccountPage && !managedServicePremium ? -1 : 0}
      aria-disabled={isPremiumAccountPage && !managedServicePremium}
    >
      <div className="flex items-center justify-between">
        <div
          className={`h-[20px] w-[20px]   lg:h-[25px] lg:w-[25px] ${
            activeSubPage === ele.name
              ? "text-white"
              : `${mainObj.color} group-hover/tab:!text-white`
          }`}
        >
          {mainObj.icon}
        </div>
        <p
          className={`select-none capitalize   ${
            activeSubPage === ele.name
              ? "text-white"
              : "!text-txtColor group-hover/tab:!text-white"
          } grow pl-3 text-[14px] font-semibold leading-none 2xl:text-[18px]`}
        >
          {label}
        </p>
        <div
          className={`flex h-[36px] min-w-[44px] items-center justify-center rounded-lg px-1   lg:h-[40px] lg:min-w-[50px] 2xl:h-[44px] 2xl:min-w-[55px] ${
            activeSubPage === ele.name
              ? "bg-white"
              : `${mainObj.bg} ${mainObj.color} group-hover/tab:!bg-white`
          }`}
        >
          <p
            className={`${mainObj.color} select-none text-[min(2vh,18px)] font-bold lg:text-[18px] 2xl:text-[20px]`}
          >
            {ele.value}
          </p>
        </div>
      </div>
    </Link>
  );
});

export const CandidateTypes = React.memo(function CandidateTypes() {
  const dispatch = useDispatch();
  const {
    typeCount,
    activeSubPage,
    clientUserAllPermissions,
    isPremiumAccount,
    user,
  } = useSelector(
    (state) => ({
      typeCount: state.clientsCandidates.typeCount,
      activeSubPage: state.clientCommon.activeSubPage,
      clientUserAllPermissions: state.session.clientUserAllPermissions,
      isPremiumAccount: state.session.isPremiumAccount,
      user: state.clientProfile.user,
    }),
    shallowEqual,
  );

  const { client_allow_publish_job } = user || {};
  const firstRender = useRef(true);

  // Fetch candidate types only on mount
  useEffect(() => {
    dispatch(fetchCandidateTypes());
  }, [dispatch]);

  // Reset all filters when activeSubPage changes (except on first render)
  const handleResetAllSearch = useCallback(() => {
    dispatch(updateAppliedFilters({ reset: true }));
    dispatch(updateInvitedFilters({ reset: true }));
    dispatch(updateBookedFilters({ reset: true }));
    dispatch(updateWorkedFilters({ reset: true }));
    dispatch(updateUnfilledFilters({ reset: true }));
    dispatch(updateBookedView(DEFAULT_BOOKED_LIST_VIEW));
    dispatch(updateWorkedView(DEFAULT_WORKED_LIST_VIEW));
  }, [dispatch]);

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    handleResetAllSearch();
  }, [activeSubPage, handleResetAllSearch]);

  // Memoize typeCount to avoid map re-renders
  const candidateTypeList = useMemo(() => {
    if (!typeCount || !Array.isArray(typeCount) || !typeCount.length)
      return null;
    return typeCount.map((ele, index) => {
      const isPremiumAccountPage =
        ele.name === "unfilled" || ele.name === "applied";
      const hasAccess = clientUserAllPermissions?.[ele.access];
      const managedServicePremium = isPremiumAccount
        ? isPremiumAccount
        : client_allow_publish_job;

      if (isPremiumAccountPage && !managedServicePremium) {
        return (
          <Fragment key={ele.name}>
            <NotAllowedButtonWrapper
              rootClass="rounded-[14px]"
              iconClass="w-[32px] h-[32px] text-primary"
            >
              <CandidateTypeLink
                ele={ele}
                activeSubPage={activeSubPage}
                clientUserAllPermissions={clientUserAllPermissions}
                isPremiumAccount={isPremiumAccount}
                client_allow_publish_job={client_allow_publish_job}
                user={user}
                dispatch={dispatch}
              />
            </NotAllowedButtonWrapper>
          </Fragment>
        );
      }
      if (hasAccess === 0 || typeof hasAccess === "undefined") {
        return (
          <Fragment key={ele.name}>
            <NoAccessButtonWrapper
              rootClass="rounded-[14px]"
              iconClass="w-[32px] h-[32px] text-primary"
              _checkPermission={[ele.access]}
            >
              <CandidateTypeLink
                ele={ele}
                activeSubPage={activeSubPage}
                clientUserAllPermissions={clientUserAllPermissions}
                isPremiumAccount={isPremiumAccount}
                client_allow_publish_job={client_allow_publish_job}
                user={user}
                dispatch={dispatch}
              />
            </NoAccessButtonWrapper>
          </Fragment>
        );
      }
      return (
        <Fragment key={ele.name}>
          <CandidateTypeLink
            ele={ele}
            activeSubPage={activeSubPage}
            clientUserAllPermissions={clientUserAllPermissions}
            isPremiumAccount={isPremiumAccount}
            client_allow_publish_job={client_allow_publish_job}
            user={user}
            dispatch={dispatch}
          />
        </Fragment>
      );
    });
  }, [
    typeCount,
    clientUserAllPermissions,
    isPremiumAccount,
    client_allow_publish_job,
    user,
    activeSubPage,
    dispatch,
  ]);

  return (
    <div className="CandidateTypes_Wrapper tour-candidate-types">
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-5">
        {candidateTypeList}
      </div>
    </div>
  );
});
